import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config';
import { getYearLabel as getYearLabelUtil } from '../utils/yearUtils';

const ParentsManager = () => {
  const [allStudents, setAllStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expandedYears, setExpandedYears] = useState([]);
  const [expandedForms, setExpandedForms] = useState([]);
  const [expandedStudents, setExpandedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Auto-expand years and forms when searching
  useEffect(() => {
    if (searchTerm && searchTerm.trim()) {
      // Find all students matching the search
      const matchingStudents = allStudents.filter(s => 
        s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.school_id?.includes(searchTerm) ||
        s.form?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Get unique years and forms from matching students
      const yearsToExpand = [...new Set(matchingStudents.map(s => getStudentYearGroup(s)).filter(y => y))];
      const formsToExpand = [...new Set(matchingStudents.map(s => {
        const year = getStudentYearGroup(s);
        return year && s.form ? `${year}-${s.form}` : null;
      }).filter(f => f))];

      setExpandedYears(yearsToExpand);
      setExpandedForms(formsToExpand);
    } else {
      // When search is cleared, collapse everything
      setExpandedYears([]);
      setExpandedForms([]);
      setExpandedStudents([]);
    }
  }, [searchTerm, allStudents]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load students
      const studentsResponse = await axios.get(buildApiUrl('/synced-students'));
      const studentsData = studentsResponse.data.students || [];
      setAllStudents(studentsData);

      // Load parents - use backend endpoint to ensure data consistency
      console.log('[ParentsManager] Fetching parents from:', buildApiUrl('/data/parents.json'));
      const parentsResponse = await axios.get(buildApiUrl('/data/parents.json'));
      console.log('[ParentsManager] Parents response:', parentsResponse);
      console.log('[ParentsManager] Parents response.data type:', typeof parentsResponse.data);
      console.log('[ParentsManager] Parents response.data is array?', Array.isArray(parentsResponse.data));
      console.log('[ParentsManager] Parents response.data length:', parentsResponse.data?.length);
      
      const parentsData = Array.isArray(parentsResponse.data) ? parentsResponse.data : [];
      setParents(parentsData);

      console.log('[ParentsManager] Loaded:', studentsData.length, 'students and', parentsData.length, 'parents');
      console.log('[ParentsManager] First parent:', parentsData[0]);
    } catch (error) {
      console.error('[ParentsManager] Error loading data:', error);
      console.error('[ParentsManager] Error details:', error.response || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncParents = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(buildApiUrl('/sync-parents'));
      if (response.data.success) {
        alert(`Successfully synced ${response.data.count} parents`);
        await loadData();
      }
    } catch (error) {
      console.error('Error syncing parents:', error);
      alert('Failed to sync parents from iSAMS');
    } finally {
      setSyncing(false);
    }
  };

  // Get unique year groups - dynamically from actual student data
  const getYearGroups = () => {
    const yearSet = new Set();
    allStudents.forEach(s => {
      const yg = getStudentYearGroup(s);
      if (yg) yearSet.add(yg);
    });
    
    // Sort in proper order
    const yearOrder = ['Pre-School', 'Reception', ...Array.from({length: 13}, (_, i) => `Year ${i + 1}`)];
    return yearOrder.filter(y => yearSet.has(y));
  };

  // Helper function to extract year group from student data
  const getStudentYearGroup = (student) => {
    // Priority 1: Use year_group if available
    if (student.year_group) {
      return student.year_group;
    }
    
    // Priority 2: Derive from form field (e.g., "3S" -> "Year 3", "R" -> "Reception", "PS Dolphins" -> "Pre-School")
    if (student.form) {
      const formStr = student.form.toString().trim();
      
      // Check for Pre-School
      if (/^PS/i.test(formStr)) {
        return 'Pre-School';
      }
      
      // Check for Reception
      if (/^R$/i.test(formStr) || /^Reception$/i.test(formStr)) {
        return 'Reception';
      }
      
      // Extract year number from form like "3S", "10A", etc.
      const yearMatch = formStr.match(/^(\d+)/);
      if (yearMatch) {
        return `Year ${yearMatch[1]}`;
      }
    }
    
    // Priority 3: Use nc_year field if available
    if (student.nc_year) {
      const ncYear = student.nc_year.toString().trim();
      return getYearLabelUtil(ncYear);
    }
    
    // Priority 4: Use year field if available
    if (student.year) {
      const year = student.year.toString().trim();
      return getYearLabelUtil(year);
    }
    
    return null;
  };

  // Get forms in a year
  const getFormsInYear = (year) => {
    const forms = [...new Set(allStudents
      .filter(s => getStudentYearGroup(s) === year)
      .map(s => s.form)
    )];
    return forms.filter(f => f).sort();
  };

  // Get students in a form
  const getStudentsInForm = (year, form) => {
    let students = allStudents.filter(s => {
      const studentYear = getStudentYearGroup(s);
      return studentYear === year && s.form === form;
    });
    
    // Apply search filter
    if (searchTerm) {
      students = students.filter(s => 
        s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.school_id?.includes(searchTerm) ||
        s.form?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return students.sort((a, b) => a.full_name.localeCompare(b.full_name));
  };

  // Get parents for a student
  const getParentsForStudent = (studentId) => {
    const matchedParents = parents.filter(p => p.child_ids && p.child_ids.includes(studentId));
    
    // Debug: Log the first time a student has no parents
    if (matchedParents.length === 0 && parents.length > 0 && !window._debuggedParentLink) {
      window._debuggedParentLink = true;
      console.log('[ParentsManager] No parents found for student:', studentId);
      console.log('[ParentsManager] Sample parent child_ids:', parents.slice(0, 3).map(p => ({ id: p.id, child_ids: p.child_ids })));
      console.log('[ParentsManager] Checking if any parent has this student...');
      const anyMatch = parents.some(p => p.child_ids && p.child_ids.some(cid => cid === studentId || cid.toString() === studentId.toString()));
      console.log('[ParentsManager] Any match found?', anyMatch);
    }
    
    return matchedParents;
  };

  // Toggle functions
  const toggleYear = (year) => {
    setExpandedYears(prev => 
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
    );
  };

  const toggleForm = (formKey) => {
    setExpandedForms(prev => 
      prev.includes(formKey) ? prev.filter(f => f !== formKey) : [...prev, formKey]
    );
  };

  const toggleStudent = (studentId) => {
    setExpandedStudents(prev => 
      prev.includes(studentId) ? prev.filter(s => s !== studentId) : [...prev, studentId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading parents data...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">👨‍👩‍👧‍👦</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Parents Manager</h1>
          </div>
          <button
            onClick={handleSyncParents}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Sync from iSAMS
          </button>
        </div>
        <p className="text-gray-600">View students and their associated parent accounts</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-500 mb-1">Total Students</div>
          <div className="text-2xl font-bold text-gray-900">{allStudents.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-500 mb-1">Total Parents</div>
          <div className="text-2xl font-bold text-gray-900">{parents.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm font-medium text-gray-500 mb-1">Year Groups</div>
          <div className="text-2xl font-bold text-gray-900">{getYearGroups().length}</div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search students by name, ID, or form..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {/* Hierarchical List */}
      <div className="bg-white rounded-lg shadow-md">
        {getYearGroups().map(year => {
          const isYearExpanded = expandedYears.includes(year);
          const formsInYear = getFormsInYear(year);
          
          return (
            <div key={year} className="border-b border-gray-200 last:border-b-0">
              {/* Year Header */}
              <button
                onClick={() => toggleYear(year)}
                className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="font-semibold text-gray-900 flex items-center gap-2">
                  <span className="text-lg">{isYearExpanded ? '▼' : '▶'}</span>
                  {year}
                </span>
                <span className="text-sm text-gray-600">
                  {formsInYear.length} form(s)
                </span>
              </button>

              {/* Forms in Year */}
              {isYearExpanded && (
                <div className="border-t border-gray-200">
                  {formsInYear.map(form => {
                    const formKey = `${year}-${form}`;
                    const isFormExpanded = expandedForms.includes(formKey);
                    const studentsInForm = getStudentsInForm(year, form);

                    return (
                      <div key={formKey} className="border-b border-gray-100 last:border-b-0">
                        {/* Form Header */}
                        <button
                          onClick={() => toggleForm(formKey)}
                          className="w-full px-8 py-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-800 flex items-center gap-2">
                            <span>{isFormExpanded ? '▼' : '▶'}</span>
                            Form {form}
                          </span>
                          <span className="text-sm text-gray-600">
                            {studentsInForm.length} student(s)
                          </span>
                        </button>

                        {/* Students in Form */}
                        {isFormExpanded && (
                          <div className="bg-gray-50">
                            {studentsInForm.length === 0 ? (
                              <div className="px-10 py-4 text-center text-gray-500">
                                No students found
                              </div>
                            ) : (
                              studentsInForm.map(student => {
                                const isStudentExpanded = expandedStudents.includes(student.school_id);
                                const studentParents = getParentsForStudent(student.school_id);

                                return (
                                  <div key={student.school_id} className="border-b border-gray-200 last:border-b-0">
                                    {/* Student Header */}
                                    <button
                                      onClick={() => toggleStudent(student.school_id)}
                                      className="w-full px-10 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors text-left"
                                    >
                                      <div className="flex items-center gap-3">
                                        <span className="text-sm">{isStudentExpanded ? '▼' : '▶'}</span>
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                          <span className="text-blue-700 font-medium text-sm">
                                            {student.full_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                          </span>
                                        </div>
                                        <div>
                                          <div className="font-medium text-gray-900">{student.full_name}</div>
                                          <div className="text-xs text-gray-500">ID: {student.school_id}</div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                          student.gender === 'M' 
                                            ? 'bg-blue-100 text-blue-800' 
                                            : 'bg-pink-100 text-pink-800'
                                        }`}>
                                          {student.gender === 'M' ? 'Male' : 'Female'}
                                        </span>
                                        <span className="text-sm text-gray-600">
                                          {studentParents.length} parent(s)
                                        </span>
                                      </div>
                                    </button>

                                    {/* Parents List */}
                                    {isStudentExpanded && (
                                      <div className="px-14 py-3 bg-white border-t border-gray-200">
                                        {studentParents.length === 0 ? (
                                          <div className="text-center py-4 text-gray-500 text-sm">
                                            No parent accounts found for this student
                                          </div>
                                        ) : (
                                          <div className="space-y-3">
                                            {studentParents.map((parent, index) => (
                                              <div key={parent.id || index} className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                                                <div className="flex items-start gap-3">
                                                  <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <span className="text-purple-700 font-bold text-lg">
                                                      {parent.name?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                                                    </span>
                                                  </div>
                                                  <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                      {parent.title && (
                                                        <span className="text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                                                          {parent.title}
                                                        </span>
                                                      )}
                                                      <h4 className="font-semibold text-gray-900">{parent.name}</h4>
                                                    </div>
                                                    <div className="space-y-1">
                                                      <div className="flex items-center gap-2 text-sm">
                                                        <span className="text-gray-500">📧</span>
                                                        <a 
                                                          href={`mailto:${parent.email}`}
                                                          className="text-blue-600 hover:text-blue-800 truncate"
                                                        >
                                                          {parent.email}
                                                        </a>
                                                      </div>
                                                      <div className="flex items-center gap-2 text-sm">
                                                        <span className="text-gray-500">👤</span>
                                                        <span className="text-gray-700">
                                                          Username: <span className="font-medium">{parent.email}</span>
                                                        </span>
                                                      </div>
                                                      <div className="flex items-center gap-2 text-sm">
                                                        <span className="text-gray-500">🔑</span>
                                                        <span className="text-gray-700">
                                                          Password: <span className="font-medium">{parent.password}</span>
                                                        </span>
                                                      </div>
                                                      {parent.child_ids && parent.child_ids.length > 1 && (
                                                        <div className="flex items-center gap-2 text-xs text-gray-600">
                                                          <span>👥</span>
                                                          <span>Parent of {parent.child_ids.length} students</span>
                                                        </div>
                                                      )}
                                                      <div className="text-xs text-gray-500">
                                                        ID: {parent.id}
                                                      </div>
                                                      <div className="mt-3 pt-3 border-t border-purple-200">
                                                        <button
                                                          onClick={() => {
                                                            // Clear current session first
                                                            localStorage.removeItem('role');
                                                            localStorage.removeItem('token');
                                                            localStorage.removeItem('teacherEmail');
                                                            localStorage.removeItem('teacherName');
                                                            
                                                            // Store parent credentials for auto-login
                                                            localStorage.setItem('troubleshoot_username', parent.email);
                                                            localStorage.setItem('troubleshoot_password', parent.password);
                                                            localStorage.setItem('troubleshoot_userType', 'Parent');
                                                            
                                                            // Redirect to parent login page
                                                            window.location.href = '/login-parent';
                                                          }}
                                                          className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                                        >
                                                          <span>🔓</span>
                                                          <span>Open Parent Account</span>
                                                        </button>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {allStudents.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
          <p className="text-lg font-medium mb-2">No data available</p>
          <p className="text-sm">Student and parent data will appear here</p>
        </div>
      )}
    </div>
  );
};

export default ParentsManager;
